import { FC, useRef, useState } from 'react';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { MetricBreakdownDetail } from 'hooks/useRoiAppointmentMetricBreakdown';
import { COLOR_CODES } from 'utils/constants';

ChartJS.register(ArcElement, Tooltip, Legend);

interface TooltipState {
  visible: boolean;
  x: number;
  y: number;
  label: string;
  value: number;
  pct: string;
  color: string;
}

interface MetricDonutChartProps {
  details: MetricBreakdownDetail[];
  unitLabel: string;
}

const MetricDonutChart: FC<MetricDonutChartProps> = ({
  details,
  unitLabel,
}) => {
  const [tooltip, setTooltip] = useState<TooltipState>({
    visible: false,
    x: 0,
    y: 0,
    label: '',
    value: 0,
    pct: '',
    color: '',
  });
  const chartContainerRef = useRef<HTMLDivElement>(null);

  const total = details.reduce((sum, d) => sum + d.value, 0);
  const isEmpty = total === 0;

  const getColor = (index: number) =>
    details[index]?.color ?? COLOR_CODES[index];

  const chartData = {
    labels: isEmpty ? ['No data'] : details.map((d) => d.label),
    datasets: [
      {
        data: isEmpty ? [1] : details.map((d) => d.value),
        backgroundColor: isEmpty
          ? ['#E8E8E8']
          : details.map((_, i) => getColor(i)),
        borderColor: '#FFFFFF',
        borderWidth: isEmpty ? 0 : 1,
        hoverBorderColor: '#FFFFFF',
        hoverBorderWidth: isEmpty ? 0 : 1,
        hoverOffset: 0,
      },
    ],
  };

  return (
    <div className="flex items-center justify-center gap-x-8">
      <div
        ref={chartContainerRef}
        className="relative w-[28rem] h-[28rem] shrink-0"
      >
        <Doughnut
          data={chartData}
          options={{
            cutout: '60%',
            maintainAspectRatio: false,
            plugins: {
              legend: { display: false },
              tooltip: {
                enabled: false,
                external: isEmpty
                  ? undefined
                  : ({ chart, tooltip: tip }) => {
                      if (tip.opacity === 0) {
                        setTooltip((prev) =>
                          prev.visible ? { ...prev, visible: false } : prev
                        );
                        return;
                      }

                      const dataIndex = tip.dataPoints?.[0]?.dataIndex ?? 0;
                      const value = details[dataIndex]?.value ?? 0;
                      const pct =
                        total > 0
                          ? ((value / total) * 100).toFixed(1)
                          : '0.0';
                      const label = details[dataIndex]?.label ?? '';
                      const color = getColor(dataIndex) ?? '#B01BAE';

                      const containerRect =
                        chartContainerRef.current?.getBoundingClientRect();
                      const canvasRect = chart.canvas.getBoundingClientRect();

                      const x =
                        canvasRect.left -
                        (containerRect?.left ?? 0) +
                        tip.caretX;
                      const y =
                        canvasRect.top - (containerRect?.top ?? 0) + tip.caretY;

                      setTooltip((prev) => {
                        if (
                          prev.visible &&
                          prev.x === x &&
                          prev.y === y &&
                          prev.label === label &&
                          prev.value === value &&
                          prev.pct === pct &&
                          prev.color === color
                        ) {
                          return prev;
                        }
                        return {
                          visible: true,
                          x,
                          y,
                          label,
                          value,
                          pct,
                          color,
                        };
                      });
                    },
              },
            },
          }}
        />

        {tooltip.visible && (
          <div
            className="absolute z-20 pointer-events-none bg-white text-darkest-grey text-14 leading-[2.1rem] px-1.6 py-1.2 rounded-[1rem] shadow-primary whitespace-nowrap"
            style={{
              left: tooltip.x,
              top: tooltip.y,
              transform: 'translate(0, -110%)',
            }}
          >
            <div className="font-bold mb-0.4">{tooltip.label}</div>
            <div className="flex items-center gap-x-0.8">
              <span
                className="w-0.8 h-0.8 rounded-full shrink-0"
                style={{ backgroundColor: tooltip.color }}
              />
              <span>
                {tooltip.value.toLocaleString()} {unitLabel}
              </span>
              <span className="text-mid-grey">({tooltip.pct}%)</span>
            </div>
          </div>
        )}
      </div>

      {!isEmpty && (
        <div className="grid grid-cols-[auto_auto] gap-y-1.2 gap-x-4">
          {details.map((d, i) => (
            <div key={d.label} className="flex items-start gap-x-0.8">
              <span
                className="w-1.2 h-1.2 rounded-full shrink-0 mt-[0.45rem]"
                style={{ backgroundColor: getColor(i) }}
              />
              <span className="text-14 leading-[2.1rem] text-darkest-grey max-w-[24rem]">
                {d.label}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MetricDonutChart;
